/* eslint-disable @next/next/no-img-element */
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Download, Loader2, Share2 } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import CustomModalLayoutV2 from "../../../../components/bidder/Tabs/auctionFloor/components/CustomModalLayoutV2";
import {
  PropertyShareFormat,
  PropertyShareType,
  usePropertyShare,
} from "./hooks/usePropertyShare";
import { usePDAuctionDetails } from "../../propertyDetail/hooks/usePDAuctionDetails";
import { usePrivatePDAuctionDetails } from "../../postYourProperty/detailsPage/hooks/usePrivatePDAuctionDetails";
import { PropertyType } from "../../../../hoc";
import { KeyValueCard } from "../../../../shadcn/components/custom/KeyValueCard";
import clsx from "clsx";
import {
  FULL_LOGO_WITH_TRADEMARK,
  getWatermarkedImgixUrl,
  maskSellerName,
} from "services";
import { getPrivatePropertyArea } from "../../postYourProperty/listingPageV2/utils/transformPrivateProperties";

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  propertyData: any;
  propertyType: PropertyType;
}

// Export owner role types for use across the application
export type AuctionPropertyOwnerRoles = "bank";
export type PrivatePropertyOwnerRoles =
  | "authorized_officer"
  | "appointed_agent"
  | "broker"
  | "owner"
  | "cirp_professional"
  | "property_agent";
export type PropertyOwnerRoleType =
  | AuctionPropertyOwnerRoles
  | PrivatePropertyOwnerRoles;

// Helper function to get display name for seller type
export const getSellerTypeDisplay = (ownerType: string): string => {
  const typeMap: Record<string, string> = {
    bank: "Bank",
    authorized_officer: "Authorized Officer",
    appointed_agent: "Appointed Agent",
    broker: "Broker",
    owner: "Owner",
    cirp_professional: "CIRP Professional",
    property_agent: "Property Agent",
    test: "Test",
  };
  return typeMap[ownerType?.toLowerCase()] || "Owner";
};

// Helper function to format area display
const formatArea = (propertyData: any): string => {
  if (propertyData?.property_listing_type === "auction-property") {
    if (propertyData?.area) {
      return `${propertyData.area} sq ft`;
    }
  } else {
    // For private properties, we have a helper function that checks multiple sources and also sets the area on the main object for consistency
    return getPrivatePropertyArea(propertyData);
  }

  return "N/A";
};

// Helper function to get location
const formatLocation = (propertyData: any): string => {
  // Check various data sources for location info
  const city =
    propertyData?.city?.name ||
    propertyData?.location?.city ||
    propertyData?.posted_properties_raw_data?.city?.label ||
    propertyData?.posted_properties_raw_data?.city ||
    propertyData?.posted_properties_data?.city;
  const locality =
    propertyData?.locality?.name ||
    propertyData?.location?.locality ||
    propertyData?.posted_properties_raw_data?.locality;
  const state =
    propertyData?.state?.name ||
    propertyData?.location?.state ||
    propertyData?.posted_properties_raw_data?.state?.label ||
    propertyData?.posted_properties_raw_data?.state;

  const parts = [locality, city, state].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : "N/A";
};

const ImageWithFallback = ({
  src,
  alt,
  className,
}: {
  src?: string;
  alt?: string;
  className?: string;
}) => {
  const [hasError, setHasError] = useState(false);

  if (!src || hasError) {
    return (
      <div className="w-[240px] h-full bg-gray-200 flex items-center justify-center">
        <span className="text-gray-500 text-sm">No Images Available</span>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      crossOrigin="anonymous"
      onError={() => setHasError(true)}
    />
  );
};

// Helper function to safely build image URL
const safeImageUrl = (path?: string, fallbackId?: string) => {
  try {
    if (!path && !fallbackId) return "";

    if (path) {
      const parsed = new URL(path);
      return `${process.env.NEXT_PUBLIC_IMAGIX_URL}/${parsed.pathname.slice(
        1,
      )}`;
    }

    return `${process.env.NEXT_PUBLIC_IMAGIX_URL}/${fallbackId}`;
  } catch {
    return "";
  }
};

const ShareModal = ({
  isOpen,
  onClose,
  propertyData,
  propertyType,
}: ShareModalProps) => {
  const actualPropertyType = useMemo(() => {
    const listingType = propertyData?.property_listing_type;
    if (listingType === "private-property") return "private_property";
    if (listingType === "auction-property") return "auction_property";
    return propertyType;
  }, [propertyData?.property_listing_type, propertyType]);

  const propertyImages = useMemo(() => {
    const isPrivateProperty =
      actualPropertyType === "private_property" ||
      propertyData?.property_listing_type === "private-property";

    if (!isPrivateProperty) {
      const images = propertyData?.images || [];

      return images.map((img: any) => ({
        id: img.uuid,
        url: safeImageUrl(img.path_to_file, img.uuid),
      }));
    } else {
      const photos =
        propertyData?.posted_properties_data?.property_photos ||
        propertyData?.posted_properties_raw_data?.property_photos ||
        [];

      if (photos.length > 0) {
        return photos.map((photo: any) => ({
          id: photo.id || photo.uuid,
          url: safeImageUrl(photo.path_to_file, photo.id || photo.uuid),
        }));
      }

      const fallbackImages = propertyData?.sub_asset_type_details?.images || [];
      return fallbackImages.map((img: any) => ({
        id: img.uuid,
        url: safeImageUrl(img.path_to_file, img.uuid),
      }));
    }
  }, [propertyData, actualPropertyType]);

  const propertyCoverImage = useMemo(
    () => ({
      auction_property: getWatermarkedImgixUrl(propertyImages?.[0]?.url) || "",
      private_property: getWatermarkedImgixUrl(propertyImages?.[0]?.url) || "",
    }),
    [propertyImages],
  );

  const idLabel = useMemo(() => {
    const listingId = propertyData?.listing_id || "N/A";

    return {
      auction_property: `Auction ID: ${listingId}`,
      private_property: `Listing ID: ${listingId}`,
    };
  }, [propertyData?.listing_id]);

  const { auctionDetails: auctionPropertyDetails } = usePDAuctionDetails({
    propertyData,
  });
  const { auctionDetails: privatePropertyDetails } = usePrivatePDAuctionDetails(
    {
      propertyData,
    },
  );

  const isLoggedIn = useSelector((state: any) => state.auth.user);

  const propertyShareData = useMemo(() => {
    const isAuctionProperty = actualPropertyType === "auction_property";

    const rawSellerName = propertyData?.borrower_name || "NA";

    const sellerName = isAuctionProperty
      ? rawSellerName
      : isLoggedIn
      ? rawSellerName
      : maskSellerName(rawSellerName);

    const ownerType =
      propertyData?.owner_type ||
      propertyData?.posted_properties_raw_data?.userType ||
      (isAuctionProperty ? "bank" : "owner");
    const sellerType = getSellerTypeDisplay(ownerType);
    const baseData = {
      uuid: propertyData?.uuid,
      title: isAuctionProperty
        ? propertyData?.asset_type?.name || propertyData?.title || "Property"
        : propertyData?.posted_properties_raw_data?.listingTitle ||
          propertyData?.title ||
          "Property",
      auctionId: propertyData?.listing_id || "unknown",
      bankName: isAuctionProperty
        ? propertyData?.bank?.name || "N/A"
        : propertyData?.posted_properties_raw_data?.userType === "broker"
        ? "Broker Listed"
        : "Owner Listed",
      reservePrice:
        propertyData?.property_date_type !== "undated"
          ? propertyData?.reserve_price
          : "Contact Us",
      emd: isAuctionProperty
        ? propertyData?.property_date_type !== "undated"
          ? propertyData?.emd_amount
          : "Contact Us"
        : undefined,
      possession: propertyData?.possession || "N/A",
      area: formatArea(propertyData),
      propertyType: isAuctionProperty
        ? propertyData?.sub_asset_type?.name ||
          propertyData?.property_type ||
          "Property"
        : propertyData?.posted_properties_raw_data?.subPropertyType?.title ||
          propertyData?.sub_asset_type?.name ||
          "Property",
      location: formatLocation(propertyData),
      image:
        propertyCoverImage[actualPropertyType] ||
        propertyCoverImage[propertyType] ||
        "",
      sellerName,
      sellerType,
      auctionDetails: isAuctionProperty
        ? auctionPropertyDetails
        : privatePropertyDetails,
    };

    return baseData;
  }, [
    propertyData,
    propertyCoverImage,
    actualPropertyType,
    propertyType,
    auctionPropertyDetails,
    isLoggedIn,
    privatePropertyDetails,
  ]);

  const auctionPropertyDescriptionDetails = useMemo(() => {
    const borrowerName = propertyData?.borrower_name || "NA";
    return [
      { label: "Borrower Name", value: borrowerName },
      {
        label: "Type",
        value: `${propertyData?.sub_asset_type?.name}`,
      },
      { label: "Area", value: formatArea(propertyData) },
      { label: "Possession", value: propertyData?.possession_type || "NA" },
    ];
  }, [
    propertyData?.borrower_name,
    propertyData?.possession_type,
    propertyData?.sub_asset_type?.name,
  ]);

  const {
    cardRef,
    state: shareState,
    share,
    reset,
  } = usePropertyShare(propertyShareData);

  useEffect(() => {
    if (!isOpen) reset();
  }, [isOpen, reset]);

  const handleShareAction = async ({
    shareType,
    format,
  }: {
    shareType: PropertyShareType;
    format: PropertyShareFormat;
  }) => {
    try {
      await share(shareType, {
        format,
        propertyUrl: window.location.href,
        uploadToS3: isLoggedIn,
      });
    } catch (error) {
      console.error("Brochure generation failed", error);
    }
  };

  return (
    <div>
      <CustomModalLayoutV2
        open={isOpen}
        onClose={onClose}
        blurBackground
        childrenClassName="pb-6 px-6 lg:px-8 overflow-y-scroll"
        className="pt-8 lg:pb-[30px] mx-4 w-full md:w-[900px] overflow-y-scroll max-h-[calc(100%-10px)]"
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-200 bg-white sticky top-0 z-10 min-h-[56px]">
            <h3 className="text-lg font-semibold text-gray-900 truncate pr-4">
              Property Brochure Preview
            </h3>

            <div className="flex items-center gap-1 sm:gap-2 -mr-1.5">
              <button
                onClick={() =>
                  handleShareAction({
                    shareType: "download-brochure",
                    format: "pdf",
                  })
                }
                disabled={shareState.isSharing}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-40"
                title="Download PDF brochure"
              >
                <Download className="w-5 h-5" />
              </button>

              <button
                onClick={() =>
                  handleShareAction({
                    shareType: "share-via-whatsapp",
                    format: "pdf",
                  })
                }
                disabled={shareState.isSharing}
                className="p-2 text-gray-600 hover:text-green-600 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-40"
                title="Share on WhatsApp"
              >
                <FaWhatsapp className="w-5 h-5" />
              </button>

              <button
                onClick={() =>
                  handleShareAction({ shareType: "share-via", format: "png" })
                }
                disabled={shareState.isSharing}
                className="p-2 text-gray-600 hover:text-brand-orange hover:bg-gray-100 rounded-full transition-colors disabled:opacity-40"
                title="Share via..."
              >
                <Share2 className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-5 relative">
            {shareState.statusMessage && (
              <div className="fixed bottom-8 left-8 bg-gray-800 text-white px-4 py-3 rounded-lg z-50 shadow-lg">
                {shareState.statusMessage}
              </div>
            )}

            {shareState.isCapturing && (
              <div className="absolute inset-0 bg-white bg-opacity-90 flex flex-col items-center justify-center z-10">
                <Loader2 className="w-8 h-8 animate-spin text-orange-500 mb-4" />
                <p className="text-gray-700 font-medium">Please Wait...</p>
              </div>
            )}

            <div className="flex justify-center mb-2">
              <div
                ref={cardRef}
                className="w-full max-w-[900px] bg-white rounded-lg overflow-hidden mx-auto "
              >
                <div className="w-full flex gap-2 flex-col items-center py-6 px-4 capitalize">
                  <img
                    src={FULL_LOGO_WITH_TRADEMARK}
                    alt="Auction Bazaar Logo"
                    className="w-[239px] h-32 object-contain mx-auto"
                  />

                  <h2 className="text-xl lg:text-2xl  text-center">
                    {`${propertyData?.sub_asset_type?.name}`} -{" "}
                    {`${propertyData?.city?.name}`}
                  </h2>
                  <p
                    className="text-lg lg:text-xl m-0 p-0 text-center"
                    style={{
                      lineHeight: 1,
                      marginTop: 0,
                      marginBottom: 0,
                    }}
                  >
                    {idLabel[actualPropertyType] || idLabel[propertyType]}
                  </p>
                </div>
                <div className="flex flex-col gap-3 pb-3">
                  <div className="w-full relative">
                    <ImageWithFallback
                      src={
                        propertyCoverImage[actualPropertyType] ||
                        propertyCoverImage[propertyType]
                      }
                      alt={propertyData?.sub_asset_type?.name || "Property"}
                      className="w-full h-64 object-cover border border-gray-200"
                    />
                  </div>

                  <PropertyDetailsKeyValueCardListView
                    propertyData={propertyData}
                    propertyType={actualPropertyType || propertyType}
                  />
                </div>
                <div className="py-3 border-t border-gray-200 capitalize">
                  <PropertyDescriptionCard
                    description={propertyData?.description || ""}
                    descriptionDetails={
                      actualPropertyType === "auction_property"
                        ? auctionPropertyDescriptionDetails
                        : undefined
                    }
                  />
                </div>

                {/* Footer */}
                <div className="bg-gray-50 px-4 py-2 text-center text-xs text-gray-600 border-t border-gray-200">
                  Contact: +91-8370969696 | Website: www.auctionbazaar.com |
                  Mail-id: support@auctionbazaar.com
                </div>
              </div>
            </div>
          </div>
        </div>
      </CustomModalLayoutV2>
    </div>
  );
};

export default ShareModal;

const PropertyDetailsKeyValueCardListView = ({
  propertyData,
  propertyType,
  className,
  isCompact = false,
}: {
  propertyData: any;
  propertyType: PropertyType;
  className?: string;
  isCompact?: boolean;
}) => {
  const { auctionDetails: auctionPropertyDetails } = usePDAuctionDetails({
    propertyData,
  });
  const { auctionDetails: privatePropertyDetails } = usePrivatePDAuctionDetails(
    {
      propertyData,
    },
  );

  const details =
    propertyType === "auction_property"
      ? auctionPropertyDetails
      : propertyType === "private_property"
      ? privatePropertyDetails
      : [];

  if (isCompact) {
    return (
      <div className={clsx("capitalize flex flex-col gap-2", className)}>
        {details?.slice(0, 8)?.map((item, index) => (
          <KeyValueCard
            key={`propertyDetails_item_${index}_${item.label}`}
            label={item.label}
            value={item.value}
          />
        ))}
      </div>
    );
  }

  return (
    <div
      className={clsx(
        "flex flex-col gap-4 lg:grid lg:grid-cols-3 capitalize",
        className,
      )}
    >
      {details?.map((item, index) => (
        <KeyValueCard
          key={`propertyDetails_item_${index}_${item.label}`}
          label={item.label}
          value={item.value}
        />
      ))}
    </div>
  );
};

const PropertyDescriptionCard = ({
  description,
  descriptionDetails: details,
}: {
  description: string;
  descriptionDetails?: { label: string; value: string }[];
}) => {
  return (
    <div className="space-y-2 capitalize">
      <h4 className="text-sm font-semibold text-gray-800">
        Property Description
      </h4>
      <p className="text-xs text-gray-600 line-clamp-4 pb-2">
        {description || "No description available."}
      </p>

      <div className="gap-y-4 sm:gap-x-6 grid grid-cols-1 sm:grid-cols-2 mt-6">
        {details?.map((detail, index) => (
          <p
            key={`PD_Detail_${detail.label}_${index}`}
            className="text-sm capitalize"
          >
            <span className="font-medium text-brand-secondaryTextLabelGrey">
              {detail.label}:{" "}
            </span>
            <span className="mt-1 font-normal text-brand-primaryTextDarkGrey">
              {detail.value}
            </span>
          </p>
        ))}
      </div>
    </div>
  );
};
